import type { ActivityEvent, ActivityEventLevel, ActivityEventSource } from '../../types/activity'

import { getOfflineDb } from '../dbFactory'
import { createId } from '../../utils/id'

export const ACTIVITY_EVENTS_UPDATED = 'sunday-activity-events-updated'

const nowIso = (): string => new Date().toISOString()

const dispatchActivityUpdated = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.dispatchEvent(new CustomEvent(ACTIVITY_EVENTS_UPDATED))
  } catch {
    // ignore
  }
}

const parseIsoMs = (value: string): number => {
  const ms = Date.parse(value)
  return Number.isFinite(ms) ? ms : 0
}

export const dexieActivityRepo = {
  async listRecent(limit = 25): Promise<ActivityEvent[]> {
    const db = getOfflineDb()
    return db.activityEvents.orderBy('createdAt').reverse().limit(limit).toArray()
  },

  async clear(): Promise<void> {
    await getOfflineDb().activityEvents.clear()
    dispatchActivityUpdated()
  },

  async log(params: {
    title: string
    detail?: string
    source?: ActivityEventSource
    level?: ActivityEventLevel
    farmId?: string
    productionUnitId?: string
    lotId?: string
    dedupeKey?: string
    incrementCount?: number
    dedupeWindowMs?: number
  }): Promise<void> {
    const db = getOfflineDb()
    const createdAt = nowIso()
    const level = params.level ?? 'info'
    const source = params.source ?? 'local'
    const dedupeKey = params.dedupeKey?.trim() || undefined
    const dedupeWindowMs = params.dedupeWindowMs ?? 30_000
    const increment = params.incrementCount ?? 1

    const pruneIfNeeded = async (): Promise<void> => {
      const total = await db.activityEvents.count()
      if (total <= 250) return
      const toDelete = Math.max(0, total - 200)
      if (toDelete === 0) return
      const ids = await db.activityEvents.orderBy('createdAt').limit(toDelete).primaryKeys()
      if (ids.length > 0) {
        await db.activityEvents.bulkDelete(ids)
      }
    }

    const shouldMerge = async (): Promise<ActivityEvent | null> => {
      if (!dedupeKey) return null
      const existing = await db.activityEvents
        .where('[dedupeKey+createdAt]')
        .between([dedupeKey, ''], [dedupeKey, '9999'], true, true)
        .last()

      if (!existing) return null
      const ageMs = Date.now() - parseIsoMs(existing.createdAt)
      return ageMs <= dedupeWindowMs ? existing : null
    }

    await db.transaction('rw', db.activityEvents, async () => {
      const mergeTarget = await shouldMerge()
      if (mergeTarget) {
        const nextCount = (mergeTarget.count ?? 1) + increment
        await db.activityEvents.update(mergeTarget.id, {
          createdAt,
          title: params.title,
          detail: params.detail,
          level,
          source,
          farmId: params.farmId,
          productionUnitId: params.productionUnitId,
          lotId: params.lotId,
          count: nextCount
        })
      } else {
        const event: ActivityEvent = {
          id: createId('activity'),
          createdAt,
          source,
          level,
          title: params.title,
          detail: params.detail,
          farmId: params.farmId,
          productionUnitId: params.productionUnitId,
          lotId: params.lotId,
          dedupeKey,
          count: increment > 1 ? increment : undefined
        }
        await db.activityEvents.put(event)
      }

      await pruneIfNeeded()
    })

    dispatchActivityUpdated()
  }
}
